import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Endpoint {
  endpoint: string
  method: string
  calls: number
  avg_latency: number
  error_rate: number
}

interface APIKey {
  id: string
  name: string
  key_prefix: string
  calls_today: number
  calls_month: number
  limit: number
  status: string
}

export default function APIUsagePage() {
  const [endpoints, setEndpoints] = useState<Endpoint[]>([])
  const [apiKeys, setApiKeys] = useState<APIKey[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [dailyUsage, setDailyUsage] = useState<any[]>([])

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [endRes, keysRes, statsRes, dailyRes] = await Promise.all([
      fetch(API_BASE + '/api-usage/endpoints'),
      fetch(API_BASE + '/api-usage/keys'),
      fetch(API_BASE + '/api-usage/statistics'),
      fetch(API_BASE + '/api-usage/daily')
    ])
    if (endRes.ok) setEndpoints((await endRes.json()).endpoints || [])
    if (keysRes.ok) setApiKeys((await keysRes.json()).keys || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
    if (dailyRes.ok) setDailyUsage((await dailyRes.json()).usage || [])
  }

  const getLatencyColor = (latency: number) => {
    if (latency < 200) return 'text-green-600'
    if (latency < 500) return 'text-yellow-600'
    return 'text-red-600'
  }

  const maxCalls = Math.max(...dailyUsage.map(d => d.calls), 1)

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">API Usage Analytics</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Calls (30d)</div>
              <div className="text-2xl font-bold">{statistics.total_calls_30d?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Latency</div>
              <div className={`text-2xl font-bold ${getLatencyColor(statistics.avg_latency_ms)}`}>{statistics.avg_latency_ms}ms</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Error Rate</div>
              <div className={`text-2xl font-bold ${statistics.error_rate < 1 ? 'text-green-600' : 'text-red-600'}`}>{statistics.error_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Keys</div>
              <div className="text-2xl font-bold">{statistics.active_api_keys}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Top Endpoint</div>
              <div className="text-lg font-bold text-blue-600">{statistics.top_endpoint}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Daily Usage (7 days)</h2>
            <div className="flex items-end gap-2 h-40">
              {dailyUsage.map((day, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-blue-500 rounded-t" style={{ height: `${(day.calls / maxCalls) * 100}%` }}></div>
                  <div className="text-xs text-gray-500 mt-2">{day.date?.split('-')[2]}</div>
                  <div className="text-xs font-medium">{(day.calls / 1000).toFixed(1)}k</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">API Keys</h2>
            <div className="space-y-3">
              {apiKeys.map(key => (
                <div key={key.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">{key.name}</span>
                    <span className={`px-2 py-1 rounded text-xs ${key.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{key.status}</span>
                  </div>
                  <div className="text-xs text-gray-500 mb-2">{key.key_prefix}...</div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 rounded-full" style={{ width: `${(key.calls_month / key.limit) * 100}%` }}></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{key.calls_month.toLocaleString()} / {key.limit.toLocaleString()}</span>
                    <span>{((key.calls_month / key.limit) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Endpoint Statistics</h2></div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Calls</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Latency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {endpoints.map((ep, idx) => (
                <tr key={idx}>
                  <td className="px-6 py-4 font-mono text-sm">{ep.endpoint}</td>
                  <td className="px-6 py-4"><span className={`px-2 py-1 rounded text-xs ${ep.method === 'GET' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{ep.method}</span></td>
                  <td className="px-6 py-4">{ep.calls.toLocaleString()}</td>
                  <td className={`px-6 py-4 ${getLatencyColor(ep.avg_latency)}`}>{ep.avg_latency}ms</td>
                  <td className={`px-6 py-4 ${ep.error_rate < 1 ? 'text-green-600' : 'text-red-600'}`}>{ep.error_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  )
}
